import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from './Modal'
import { accessControlService } from '../services/accessControlService'
import { useAuth } from '../contexts/AuthContext'
import './EmergencyOverrideModal.css'

interface EmergencyOverrideModalProps {
  isOpen: boolean
  onClose: () => void
  patientId: string
  fields: string[]
  onSuccess?: () => void
}

const FIELD_LABELS: Record<string, string> = {
  medications: 'Medications',
  conditions: 'Conditions',
  'sensitive-documents': 'Sensitive Documents',
  'vital-signs': 'Vital Signs',
  'lab-results': 'Lab Results',
  allergies: 'Allergies',
  'family-history': 'Family History',
}

export function EmergencyOverrideModal({
  isOpen,
  onClose,
  patientId,
  fields,
  onSuccess,
}: EmergencyOverrideModalProps) {
  const { canOverrideAccess } = useAuth()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  
  console.log('[EmergencyOverrideModal] Component rendered, isOpen:', isOpen, 'patientId:', patientId, 'fields:', fields)

  const requestMutation = useMutation({
    mutationFn: (reason: string) =>
      accessControlService.requestEmergencyAccess(fields, patientId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergency-override', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-medications', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
      setReason('')
      setError(null)
      onSuccess?.()
      onClose()
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Failed to request emergency access')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    console.log('[EmergencyOverrideModal] Submitting request:', { fields, patientId, reason: reason.trim() })

    if (!reason.trim()) {
      setError('Please provide a reason for emergency access')
      return
    }

    if (!canOverrideAccess()) {
      console.error('[EmergencyOverrideModal] User does not have override permission')
      setError('You do not have permission to request emergency access')
      return
    }

    console.log('[EmergencyOverrideModal] Calling requestMutation.mutate')
    requestMutation.mutate(reason.trim())
  }

  const hasOverridePermission = canOverrideAccess()
  console.log('[EmergencyOverrideModal] Component render - isOpen:', isOpen, 'hasOverridePermission:', hasOverridePermission, 'patientId:', patientId, 'fields:', fields)
  
  // Early return if modal is not open
  if (!isOpen) {
    console.log('[EmergencyOverrideModal] Modal is not open, returning null')
    return null
  }
  
  // Check permission - if false, still render but show error
  if (!hasOverridePermission) {
    console.warn('[EmergencyOverrideModal] User does not have override permission')
    // Still render the modal so user can see why they can't request access
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Request Emergency Access"
        size="medium"
      >
        <div className="form-error" role="alert">
          You do not have permission to request emergency access. Please contact an administrator.
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Request Emergency Access"
      size="medium"
    >
      <form onSubmit={handleSubmit} className="emergency-override-form">
        <div className="form-group">
          <p className="form-description">
            You are requesting emergency access to the following restricted information:
          </p>
          <ul className="requested-fields-list">
            {fields.map((field) => (
              <li key={field}>
                <strong>{FIELD_LABELS[field] || field}</strong>
              </li>
            ))}
          </ul>
        </div>

        <div className="form-group">
          <label htmlFor="override-reason" className="required">
            Reason for Emergency Access
          </label>
          <textarea
            id="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            required
            placeholder="Please provide a detailed reason for requesting emergency access to this restricted information..."
            aria-describedby={error ? 'override-error' : undefined}
            aria-invalid={error ? 'true' : 'false'}
          />
          <p className="form-hint">
            This request will be logged and may require approval from an administrator.
          </p>
        </div>

        {error && (
          <div id="override-error" className="form-error" role="alert">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={requestMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={requestMutation.isPending || !reason.trim()}
          >
            {requestMutation.isPending ? 'Requesting...' : 'Request Emergency Access'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default EmergencyOverrideModal
